import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { PageSpinner, EmptyState } from "../components/ui.js";
import { ProposalsCard } from "../components/ProposalsCard.js";
import { IconSparkles } from "../components/icons.js";

interface Msg { id: string; role: "user" | "assistant"; content: string }
interface Insight { title: string; detail: string; kind: string }

export function InsightsPage() {
  const [tab, setTab] = useState<"coach" | "insights" | "weekly">("coach");
  return (
    <div>
      <div className="page-header">
        <h1>Insights</h1>
        <p className="subtitle">Your coach and what your data is actually saying.</p>
      </div>
      <div className="segmented" style={{ marginBottom: "var(--space-5)" }}>
        {([["coach", "Coach"], ["insights", "Insights"], ["weekly", "Weekly review"]] as const).map(([k, l]) => (
          <button key={k} className={tab === k ? "active" : ""} onClick={() => setTab(k)} type="button">{l}</button>
        ))}
      </div>
      {tab === "coach" && <CoachTab />}
      {tab === "insights" && <InsightsTab />}
      {tab === "weekly" && <WeeklyTab />}
    </div>
  );
}

function CoachTab() {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [hasProvider, setHasProvider] = useState(true);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ messages: Msg[]; hasProvider: boolean }>("/api/coach");
      setMessages(data.messages);
      setHasProvider(data.hasProvider);
    } catch {
      /* ignore */
    } finally {
      setLoaded(true);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: text }]);
    setSending(true);
    try {
      const res = await api.post<{ reply: string }>("/api/coach", { message: text });
      setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: res.reply }]);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "The coach is unavailable right now.";
      setMessages((m) => [...m, { id: `e-${Date.now()}`, role: "assistant", content: msg }]);
    } finally {
      setSending(false);
    }
  }

  if (!loaded) return <PageSpinner />;

  return (
    <div className="coach">
      {!hasProvider && (
        <div className="alert alert-info">The AI coach needs an AI provider configured. Everything else works without it — an administrator can enable it in Admin → AI.</div>
      )}
      <div className="coach-thread">
        {messages.length === 0 ? (
          <EmptyState icon={<IconSparkles />} title="Ask your coach anything">
            “What should I focus on this week?” · “I can’t get to the gym — what now?” · “My family’s ordering pizza, help.” Your coach knows your phase, plan, and what you’ve told it (and only what you’ve permitted).
          </EmptyState>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`bubble ${m.role}`}>{m.content}</div>
          ))
        )}
        {sending && <div className="bubble assistant dim">…thinking</div>}
        <div ref={endRef} />
      </div>
      <div className="coach-input">
        <textarea
          className="textarea"
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Message your coach…"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }}
          style={{ minHeight: 46, resize: "none" }}
        />
        <button className="btn btn-primary" onClick={send} disabled={sending || !input.trim()}>Send</button>
      </div>
    </div>
  );
}

function InsightsTab() {
  const [insights, setInsights] = useState<Insight[] | null>(null);
  useEffect(() => { api.get<{ insights: Insight[] }>("/api/insights").then((r) => setInsights(r.insights)).catch(() => setInsights([])); }, []);
  if (!insights) return <PageSpinner />;
  return (
    <div className="stack">
      <ProposalsCard showGenerate />
      {insights.length === 0 ? (
        <EmptyState icon={<IconSparkles />} title="Not enough data yet">Log a few weigh-ins, walks, or meals and real, data-grounded observations will appear here — never made-up trends.</EmptyState>
      ) : (
        <>
      {insights.map((i, idx) => (
        <div className="card" key={idx}>
          <div style={{ fontWeight: 600 }}>{i.title}</div>
          <p className="muted text-sm" style={{ marginTop: 4, marginBottom: 0 }}>{i.detail}</p>
        </div>
      ))}
          <p className="dim text-xs center">Observations from your real data — not causal claims.</p>
        </>
      )}
    </div>
  );
}

function WeeklyTab() {
  const [review, setReview] = useState<{ narrative: string; narrativeSource: string; data: Record<string, unknown> } | null>(null);
  const [loading, setLoading] = useState(false);
  async function generate() {
    setLoading(true);
    try { setReview(await api.post("/api/reviews/weekly")); } catch { /* ignore */ } finally { setLoading(false); }
  }
  return (
    <div className="stack">
      <button className="btn btn-primary" onClick={generate} disabled={loading}>{loading ? "Writing your review…" : "Generate this week's review"}</button>
      {review && (
        <div className="card card-pad-lg">
          <p style={{ lineHeight: 1.6 }}>{review.narrative}</p>
          {review.narrativeSource !== "assembled" && <p className="dim text-xs mt-4" style={{ marginBottom: 0 }}>Written by {review.narrativeSource} from your data.</p>}
        </div>
      )}
    </div>
  );
}
